import { DateRangeFilter, FilterList, SingleSelectSimpleFilter } from "@dash/filters";
import { Button } from "@dash/form";
import { Page } from "@dash/page";
import { QuerySkeleton, Skeleton } from "@dash/skeleton";
import { keyframes, useTheme } from "@emotion/react";
import { parseISO } from "date-fns";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { MdArrowBack, MdDeleteOutline, MdRefresh, MdTerminal } from "react-icons/md";
import { Link, useParams, useSearchParams } from "react-router-dom-v5-compat";

import { IamAuthContext } from "@megaron/auth-react";
import { ReportCell, ReportCellResult, reportEditorRoles } from "@megaron/analytics-contracts";
import { getMegaraxPath } from "@megaron/megarax-uri";
import { useClientManager } from "@megaron/react-clients";
import { Uuid } from "@megaron/uuid";

import {
  currentSpan,
  dateRangeLabels,
  periodFromSearchParams,
  periodToSearchParams,
  ReportDateRange,
  reportDateRanges,
  ReportPeriod,
  toIsoDate,
} from "./dateRanges";
import { DownloadReportMenu } from "./DownloadReportMenu";
import { ExportFormat, exportReport } from "./exportReport";
import { CellResult } from "./notebook/CellResult";
import { NotebookView } from "./notebook/NotebookView";
import { RemoveReportDialog } from "./RemoveReportDialog";

const gap = "1rem";
const titleLineHeight = "1.95rem";
const descriptionLineHeight = "1.3125rem";
const chartPlaceholderHeight = "260px";
const kpiPlaceholderHeight = "100px";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const ReportPage: React.FC = () => {
  const theme = useTheme();
  const { reportId } = useParams<{ reportId: string }>();
  const reportQuery = useClientManager("analytics")
    .reportQuery()
    .useQuery({ uuid: reportId as Uuid });
  const report = reportQuery.data;
  const [searchParams, setSearchParams] = useSearchParams();
  const period = useMemo(() => periodFromSearchParams(searchParams), [searchParams]);
  const [start, end] = currentSpan(period);
  const outputsQuery = useClientManager("analytics")
    .reportOutputsQuery()
    .useQuery(
      { uuid: reportId as Uuid, start, end },
      { refetchOnWindowFocus: false, staleTime: 5 * 60_000, keepPreviousData: true },
    );
  const results = useMemo(
    () => new Map(outputsQuery.data?.map((result) => [result.uuid, result])),
    [outputsQuery.data],
  );
  const isRefreshing = outputsQuery.isFetching;
  const isRunning = !report || isRefreshing;
  const content = useRef<HTMLDivElement>(null);
  const canEdit =
    useContext(IamAuthContext).iamUser?.roles.some((role) => reportEditorRoles.some((editor) => editor === role)) ??
    false;
  const [draft, setDraft] = useState<ReportCell[] | null>(null);
  const [isNotebook, setIsNotebook] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const save = useClientManager("analytics")
    .saveReport()
    .useMutation({
      onSuccess: async (_, __, { queryClient }) => {
        await Promise.all([
          queryClient.invalidateQueries(reportQuery.key),
          queryClient.invalidateQueries(outputsQuery.key),
        ]);
      },
    });

  const openNotebook = () => {
    if (!report) return;
    setDraft(report.cells);
    setIsNotebook(true);
  };
  const closeNotebook = () => {
    setIsNotebook(false);
    setDraft(null);
  };
  const runNotebook = () => {
    if (!report || !draft) return;
    if (JSON.stringify(draft) === JSON.stringify(report.cells)) outputsQuery.refetch();
    else save.mutate({ ...report, cells: draft });
  };

  const reportTitle = report?.title;
  useEffect(() => {
    if (reportTitle) document.title = reportTitle;
  }, [reportTitle]);

  const download = (format: ExportFormat) => {
    if (!report || !content.current) return;
    exportReport(
      format,
      {
        title: report.title,
        description: report.description,
        period: { start, end },
        cells: report.cells.map((cell) => ({
          uuid: cell.uuid,
          title: cell.title,
          code: cell.code,
          result: results.get(cell.uuid) ?? null,
        })),
      },
      content.current,
    );
  };

  const changePeriod = (next: ReportPeriod) => setSearchParams(periodToSearchParams(next), { replace: true });

  const selectDateRange = (dateRange: ReportDateRange) =>
    changePeriod({
      dateRange,
      customRange: dateRange === "custom" ? (period.customRange ?? { start, end }) : null,
    });

  return (
    <Page css={{ maxWidth: "1680px", paddingBottom: "2rem" }}>
      <div css={{ display: "flex", flexDirection: "column", paddingTop: "1rem" }}>
        <Link
          to={getMegaraxPath.uri("megarax://analytics/reports")}
          css={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.25rem",
            fontSize: "0.8125rem",
            color: theme.colors.secondaryText,
            alignSelf: "flex-start",
          }}
        >
          <MdArrowBack /> Raporty
        </Link>

        <div css={{ display: "flex", alignItems: "flex-start", gap: "1rem", marginTop: "0.5rem", flexWrap: "wrap" }}>
          <div css={{ flex: "1 1 320px", minWidth: 0, display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            {report ? (
              <h1
                css={{
                  margin: 0,
                  fontFamily: theme.displayFontFamily,
                  fontSize: "1.625rem",
                  fontWeight: 700,
                  color: theme.colors.primary,
                  lineHeight: titleLineHeight,
                }}
              >
                {report.title}
              </h1>
            ) : (
              <TextLine height={titleLineHeight} width="min(360px, 70%)" />
            )}
            {report ? (
              <p
                css={{
                  margin: 0,
                  color: theme.colors.secondaryText,
                  fontSize: "0.9375rem",
                  lineHeight: descriptionLineHeight,
                }}
              >
                {report.description}
              </p>
            ) : (
              <TextLine height={descriptionLineHeight} width="min(560px, 90%)" />
            )}
            {outputsQuery.isError && (
              <span css={{ fontSize: "0.75rem", color: theme.colors.danger }}>Nie udało się przeliczyć raportu</span>
            )}
          </div>
          <Button
            variant="outline"
            icon={<MdRefresh />}
            onClick={() => outputsQuery.refetch()}
            isDisabled={isRunning}
            isLoading={isRefreshing}
          >
            {isRefreshing ? "Przeliczanie…" : "Odśwież"}
          </Button>
          {canEdit && !isNotebook && (
            <Button variant="outline" icon={<MdTerminal />} onClick={openNotebook} isDisabled={!report}>
              Edytuj notebook
            </Button>
          )}
          <DownloadReportMenu onDownload={download} isDisabled={isRunning} />
          {canEdit && (
            <Button
              variant="outline"
              icon={<MdDeleteOutline />}
              onClick={() => setIsRemoving(true)}
              isDisabled={!report}
              css={{ color: theme.colors.danger, borderColor: theme.colors.danger }}
            >
              Usuń
            </Button>
          )}
        </div>
        {save.isError && (
          <span css={{ fontSize: "0.75rem", color: theme.colors.danger }}>Nie udało się zapisać raportu</span>
        )}

        <FilterList>
          <SingleSelectSimpleFilter<ReportDateRange>
            label="Okres"
            value={period.dateRange}
            options={reportDateRanges.map((r) => ({ value: r, label: dateRangeLabels[r] }))}
            onChange={(value) => value && selectDateRange(value)}
            isUnselectDisabled
          />
          {period.dateRange === "custom" && (
            <DateRangeFilter
              label="Wybierz daty"
              startDate={parseISO(start)}
              endDate={parseISO(end)}
              onChange={({ startDate, endDate }) => {
                if (!startDate && !endDate) return selectDateRange("last30Days");
                changePeriod({
                  dateRange: "custom",
                  customRange: {
                    start: toIsoDate(startDate ?? parseISO(start)),
                    end: toIsoDate(endDate ?? parseISO(end)),
                  },
                });
              }}
            />
          )}
        </FilterList>

        <div ref={content}>
          <QuerySkeleton query={reportQuery} customSekelton={<CellGridSkeleton />}>
            {(report) => {
              if (isNotebook && draft)
                return (
                  <NotebookView
                    cells={draft}
                    savedCells={report.cells}
                    results={results}
                    isRunning={isRefreshing}
                    isSaving={save.isLoading}
                    onChange={setDraft}
                    onRun={runNotebook}
                    onClose={closeNotebook}
                  />
                );
              return (
                <CellGrid>
                  {report.cells
                    .filter((cell) => cell.visible)
                    .map((cell) => (
                      <CellView key={cell.uuid} cell={cell} result={results.get(cell.uuid)} isRunning={isRunning} />
                    ))}
                </CellGrid>
              );
            }}
          </QuerySkeleton>
        </div>
      </div>

      {isRemoving && report && <RemoveReportDialog report={report} onClose={() => setIsRemoving(false)} />}
    </Page>
  );
};

const CellGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();

  return (
    <div
      css={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "stretch",
        gap,
        paddingBottom: "3rem",
        "& > *": { flex: "0 0 100%", minWidth: 0 },
        "& > [data-width='half']": { flex: `0 0 calc((100% - ${gap}) / 2)` },
        "& > [data-width='third']": { flex: `0 0 calc((100% - 2 * ${gap}) / 3)` },
        "& > [data-width='quarter']": { flex: `0 0 calc((100% - 3 * ${gap}) / 4)` },
        [`@media (max-width: ${theme.desktopMinWidth})`]: {
          "& > [data-width='third'], & > [data-width='quarter']": { flex: `0 0 calc((100% - ${gap}) / 2)` },
        },
        [`@media (max-width: ${theme.tabletMinWidth})`]: {
          "& > [data-width='half'], & > [data-width='third']": { flex: "0 0 100%" },
        },
      }}
    >
      {children}
    </div>
  );
};

const CellGridSkeleton: React.FC = () => (
  <CellGrid>
    <CellFrame width="full" title={null}>
      <BodySkeleton height={kpiPlaceholderHeight} />
    </CellFrame>
    <CellFrame width="full" title={null}>
      <BodySkeleton height={chartPlaceholderHeight} />
    </CellFrame>
    <CellFrame width="half" title={null}>
      <BodySkeleton height={chartPlaceholderHeight} />
    </CellFrame>
    <CellFrame width="half" title={null}>
      <BodySkeleton height={chartPlaceholderHeight} />
    </CellFrame>
  </CellGrid>
);

const CellView: React.FC<{
  cell: ReportCell;
  result: ReportCellResult | undefined;
  isRunning: boolean;
}> = ({ cell, result, isRunning }) => {
  return (
    <CellFrame width={cell.width} title={cell.title}>
      {isRunning && !result ? (
        <BodySkeleton height={chartPlaceholderHeight} />
      ) : (
        <div
          css={{
            opacity: isRunning ? 0.5 : 1,
            transition: "opacity 120ms",
            animation: `${fadeIn} 200ms ease-out`,
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        >
          {result && <CellResult result={result} />}
        </div>
      )}
    </CellFrame>
  );
};

const CellFrame: React.FC<{
  width: ReportCell["width"];
  title?: string | null;
  children: React.ReactNode;
}> = ({ width, title, children }) => {
  const theme = useTheme();

  return (
    <section
      data-width={width}
      css={{
        background: "white",
        border: "1px solid rgba(0, 0, 0, 0.08)",
        borderRadius: theme.mediumBorderRadius,
        padding: "1rem 1.25rem 1.25rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.625rem",
      }}
    >
      {title === null ? (
        <TextLine height="1.25rem" width="180px" />
      ) : (
        title && (
          <h3
            css={{
              margin: 0,
              fontSize: "0.9375rem",
              fontWeight: 600,
              color: "#1d1c1a",
              lineHeight: "1.25rem",
            }}
          >
            {title}
          </h3>
        )
      )}
      {children}
    </section>
  );
};

const BodySkeleton: React.FC<{ height: string }> = ({ height }) => {
  const theme = useTheme();

  return <Skeleton height={height} css={{ display: "block", borderRadius: theme.smallBorderRadius }} />;
};

const TextLine: React.FC<{ height: string; width: string }> = ({ height, width }) => (
  <span css={{ height, display: "flex", alignItems: "center" }}>
    <Skeleton width={width} height="60%" css={{ borderRadius: "4px" }} />
  </span>
);
